import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { UserRole } from '../types'

// Fallback caso a empresa ainda não tenha assinatura/plano carregado.
export const DEFAULT_MAX_USERS = 5

export interface TeamUser {
  id: string
  email: string
  role: UserRole
  full_name: string | null
  onboarded: boolean
  created_at: string
}

export function useTeamUsers() {
  const [users, setUsers] = useState<TeamUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [myId, setMyId] = useState<string | null>(null)
  const [myRole, setMyRole] = useState<UserRole | null>(null)
  const [maxUsers, setMaxUsers] = useState<number>(DEFAULT_MAX_USERS)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }
    setMyId(user.id)

    const { data: me } = await supabase
      .from('profiles').select('role').eq('id', user.id).single()
    setMyRole((me?.role as UserRole) ?? null)

    const { data: planMax } = await supabase.rpc('current_plan_max_users')
    if (typeof planMax === 'number') setMaxUsers(planMax)

    const { data, error: e } = await supabase.rpc('list_team_users')

    if (e) { setError(e.message); setUsers([]) }
    else setUsers(((data ?? []) as TeamUser[]).sort(
      (a, b) => a.created_at.localeCompare(b.created_at)
    ))
    setLoading(false)
  }, [])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  const invoke = async (fn: string, body: Record<string, unknown>): Promise<{ ok: boolean; error?: string }> => {
    const { data, error: e } = await supabase.functions.invoke<{ ok: boolean; error?: string }>(
      fn, { body }
    )
    if (data?.ok) return { ok: true }
    if (data?.error) return { ok: false, error: data.error }
    // Tenta extrair corpo da resposta HTTP em erros 4xx/5xx
    interface SbErr { context?: { json?: () => Promise<{ error?: string }> } }
    const ctx = (e as SbErr | undefined)?.context
    if (ctx?.json) {
      try {
        const body = await ctx.json()
        if (body?.error) return { ok: false, error: body.error }
      } catch { /* noop */ }
    }
    return { ok: false, error: e?.message ?? 'Falha desconhecida' }
  }

  const invite = useCallback(async (email: string, role: 'operator' | 'admin') => {
    const r = await invoke('invite-user', { email, role })
    if (r.ok) await fetchUsers()
    return r
  }, [fetchUsers])

  const remove = useCallback(async (userId: string) => {
    const r = await invoke('remove-user', { user_id: userId })
    if (r.ok) await fetchUsers()
    return r
  }, [fetchUsers])

  const canInviteMore = users.length < maxUsers

  return { users, loading, error, myId, myRole, maxUsers, invite, remove, canInviteMore, refetch: fetchUsers }
}
